import { plain } from '../../lib/labels';
import { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  DatePicker,
  Drawer,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
  Tooltip,
} from 'antd';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DownloadOutlined } from '@ant-design/icons';
import { useUrlState, oneOf, dayParam } from '../../lib/urlState';
import { useDebounced } from '../../lib/useDebounced';
import { QueryError } from '../../lib/QueryState';
import { errMsg } from '../../lib/errors';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { formatCallCharge } from '../../lib/money';
import { fetchAllPages, downloadText, toCsv } from '../../lib/csv';
import { ListenerName, UserName, formatHandle, listenerLabel, validDisplayName } from '../../lib/listenerName';
import { CallDetailView, STATUS_COLOR, type CallDetail, type CallStatus } from './CallDetailView';
import {
  disconnectReasonLabel,
  disconnectedByColor,
  disconnectedByLabel,
  formatDuration,
  formatWhen,
} from './callDisconnect';

export interface CallRow {
  id: string;
  userId: string;
  listenerId: string;
  outcome?: string | null;
  callRequestId?: string | null;
  attemptNo?: number | null;
  userHandle?: string;
  /** The caller's own Display Name — absent on older APIs. */
  userDisplayName?: string | null;
  listenerHandle?: string;
  listenerDisplayName?: string;
  mode: 'audio' | 'video';
  status: CallStatus;
  ratePaisePerMin: string;
  /** Authoritative caller charge, whole coins. */
  totalChargedCoins?: number | null;
  /** @deprecated — fallback only when totalChargedCoins is absent. */
  totalChargedPaise: string;
  durationSeconds: number | null;
  startedAt: string | null;
  connectedAt: string | null;
  endedAt: string | null;
  agoraChannel: string;
  rtcProvider?: string;
  /** The most recent lifecycle trigger — the REAL end reason. */
  endTrigger?: string | null;
  /**
   * The last call-setup stage the client completed, when it reported one.
   * Present on an abandoned setup: the step AFTER this is where it failed.
   */
  failureStage?: string | null;
  failureReason?: string | null;
  /** Recorded who/why at the terminal transition; null on older calls. */
  disconnectedBy?: string | null;
  disconnectReason?: string | null;
  /** Derived: not_billable | free_trial | billed | unbilled | verification. */
  billingStatus?: string;
  /** Caller was an admin-flagged test account at call start: never billed. */
  isVerificationCall?: boolean;
  /** The ring went to a dummy (placeholder) listener: a timed attempt that never rang anyone. */
  isDummyAttempt?: boolean;
  billedMinutes?: number;
  listenerSharePaise?: string;
  platformSharePaise?: string;
}

interface CallsPage {
  items: CallRow[];
  nextCursor: string | null;
  /** Only on the first page when asked for: how many calls match per outcome. */
  outcomeCounts?: Record<string, number>;
}

/** `?verification=` on GET /admin/calls; `all` is sent as no param. */
type VerificationFilter = 'all' | 'true' | 'false';
/** `?dummy=` on GET /admin/calls; `all` is sent as no param. */
type DummyFilter = 'all' | 'true' | 'false';

/** Rows fetched per request. The API caps `limit` at 100. */
const PAGE_SIZE = 50;
/** Most rows one CSV export will fetch. */
const EXPORT_MAX_ROWS = 5000;

/** `?outcome=` on GET /admin/calls — how the attempt turned out. */
type OutcomeFilter =
  | 'connected'
  | 'unconnected'
  | 'declined'
  | 'cancelled'
  | 'no_answer'
  | 'not_connected'
  | 'setup_failed';
const OUTCOME_OPTIONS: Array<{ value: OutcomeFilter; label: string }> = [
  { value: 'connected', label: 'Connected (completed)' },
  { value: 'unconnected', label: 'Never connected (any reason)' },
  { value: 'no_answer', label: 'Missed — no answer' },
  { value: 'declined', label: 'Declined by listener' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'not_connected', label: 'Accepted, never connected' },
  { value: 'setup_failed', label: 'Setup failed' },
];

/** The calls table as CSV. The listener column is the admin display name. */
export function callsCsv(rows: CallRow[]): string {
  return toCsv(
    ['call_id', 'started_at', 'user', 'user_display_name', 'user_id', 'listener', 'listener_handle', 'listener_id', 'mode', 'status', 'ended_by', 'reason', 'duration_seconds', 'charged_coins'],
    rows.map((r) => [
      r.id,
      r.startedAt ?? '',
      formatHandle(r.userHandle) ?? '',
      validDisplayName(r.userDisplayName) ?? '',
      r.userId,
      listenerLabel({ displayName: r.listenerDisplayName, handle: r.listenerHandle, id: r.listenerId }),
      formatHandle(r.listenerHandle) ?? '',
      r.listenerId,
      r.mode,
      r.status,
      r.disconnectedBy ?? '',
      r.disconnectReason ?? '',
      r.durationSeconds ?? 0,
      r.totalChargedCoins ?? '',
    ]),
  );
}

/** A real call status, or `failed` = dropped + flagged + rejected. */
type StatusFilter = CallStatus | 'failed';
const STATUS_VALUES: readonly StatusFilter[] = [
  'failed',
  'connected',
  'connecting',
  'ringing',
  'initiated',
  'ended_normal',
  'ended_low_balance',
  'ended_dropped',
  'ended_flagged',
  'ended_rejected',
];

/** `?since=` → the ISO time the API takes. `today` = local midnight. */
function sinceIso(since: string | null): string | undefined {
  if (!since) return undefined;
  if (since === 'today') {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  const d = new Date(since);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function CallsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  // Every filter and the open call live in the URL (lib/urlState): Back from a
  // user's or listener's page lands on the same list; a refresh keeps it; the
  // dashboard's deep links (?status=failed&since=today, ?from=&to=, ?outcome=,
  // ?call=) are just URLs like any other.
  //   ?status=failed is the dashboard's "Failed calls": dropped + flagged + rejected.
  //   ?since=today (dashboard "today" figures) or an ISO time; cleared with the tag.
  //   ?userId= / ?listenerId= come from cross-links ("this user's calls").
  const { get, set } = useUrlState();
  const status = oneOf(get('status'), STATUS_VALUES);
  const since = get('since');
  const userIdFilter = get('userId') ?? '';
  const listenerIdFilter = get('listenerId') ?? '';
  // Caller @handle, listener display name / @handle, or a call id.
  const q = get('q') ?? '';
  const [qInput, setQInput] = useState(q);
  const qDebounced = useDebounced(qInput.trim(), 400);
  useEffect(() => {
    if (qDebounced !== q) set({ q: qDebounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);
  useEffect(() => setQInput(q), [q]);
  const mode = oneOf(get('mode'), ['audio', 'video'] as const);
  const outcome = oneOf(get('outcome'), OUTCOME_OPTIONS.map((o) => o.value));
  const from = dayParam(get('from'));
  const to = dayParam(get('to'));
  const verification: VerificationFilter = oneOf(get('verification'), ['true', 'false'] as const) ?? 'all';
  const dummy: DummyFilter = oneOf(get('dummy'), ['true', 'false'] as const) ?? 'all';
  // ?call=<id> opens that call's detail (pushed, so the browser Back closes it).
  const selectedId = get('call');
  const openCall = (id: string) => set({ call: id }, { push: true });
  const closeCall = () => set({ call: null });
  const [exporting, setExporting] = useState(false);
  const [forceEndOpen, setForceEndOpen] = useState(false);
  const [reason, setReason] = useState('');

  /** The current filters, as `GET /admin/calls` takes them. */
  const filterParams = useMemo(
    () => ({
      status: status || undefined,
      // An explicit date range wins over the dashboard's `since=today`.
      since: from ? new Date(`${from}T00:00:00`).toISOString() : sinceIso(since),
      until: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
      userId: userIdFilter || undefined,
      listenerId: listenerIdFilter || undefined,
      q: q || undefined,
      mode,
      outcome,
      verification: verification === 'all' ? undefined : verification,
      dummy: dummy === 'all' ? undefined : dummy,
    }),
    [status, since, from, to, userIdFilter, listenerIdFilter, q, mode, outcome, verification, dummy],
  );

  // Cursor-paged: "Load more" walks back through every matching call.
  const list = useInfiniteQuery({
    queryKey: ['admin', 'calls', filterParams],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<CallsPage>('/admin/calls', {
          params: {
            ...filterParams,
            cursor: pageParam,
            limit: PAGE_SIZE,
            // The per-outcome counts for the quick tabs ride on the first page only.
            includeOutcomeCounts: pageParam ? undefined : true,
          },
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Live view refreshes fast; a history view slowly. An infinite query
    // re-fetches EVERY loaded page on each tick, so once the admin has paged
    // back through history the polling stops (a reload brings it back).
    refetchInterval: (query) =>
      (query.state.data?.pages.length ?? 1) > 1 ? false : status === 'connected' ? 5_000 : 30_000,
  });
  const outcomeCounts = list.data?.pages[0]?.outcomeCounts;
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: CallRow[] = [];
    for (const page of list.data?.pages ?? []) {
      for (const c of page.items) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        out.push(c);
      }
    }
    return out;
  }, [list.data]);

  /** Every call matching the filters (not just the loaded pages) as a CSV. */
  async function exportCsv() {
    setExporting(true);
    try {
      const { rows: all, truncated } = await fetchAllPages<CallRow>(
        async (cursor) =>
          (await api.get<CallsPage>('/admin/calls', { params: { ...filterParams, cursor, limit: 100 } })).data,
        EXPORT_MAX_ROWS,
      );
      downloadText(`calls-${new Date().toISOString().slice(0, 10)}.csv`, callsCsv(all));
      if (truncated) message.warning(`Exported the newest ${EXPORT_MAX_ROWS.toLocaleString('en-IN')} calls — narrow the date range for the rest.`);
      else message.success(`Exported ${all.length.toLocaleString('en-IN')} calls`);
    } catch {
      message.error('Could not export the calls');
    } finally {
      setExporting(false);
    }
  }

  const detail = useQuery({
    queryKey: ['admin', 'call-detail', selectedId],
    queryFn: async () => (await api.get<CallDetail>(`/admin/calls/${selectedId}`)).data,
    enabled: !!selectedId,
  });

  const forceEnd = useMutation({
    mutationFn: async () => {
      if (!selectedId) return;
      await api.post(`/admin/calls/${selectedId}/force-end`, { reason });
    },
    onSuccess: () => {
      message.success('Call ended. Refund issued from platform_revenue.');
      setForceEndOpen(false);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'calls'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'call-detail', selectedId] });
    },
    onError: (e: unknown) => {
      message.error(errMsg(e, 'Force-end failed'));
    },
  });

  const columns: TableColumnsType<CallRow> = [
    {
      title: 'When',
      dataIndex: 'startedAt',
      render: (s: string | null) => formatWhen(s),
      width: 180,
    },
    {
      title: 'User → Listener',
      render: (_: unknown, r) => (
        <Space size={6} wrap>
          <UserName handle={r.userHandle} displayName={r.userDisplayName} userId={r.userId} />
          <span>→</span>
          <ListenerName
            listener={{ displayName: r.listenerDisplayName, handle: r.listenerHandle, id: r.listenerId }}
            listenerId={r.listenerId}
            secondary="inline"
          />
        </Space>
      ),
    },
    {
      title: 'Mode',
      dataIndex: 'mode',
      render: (m: string) => <Tag>{plain(m)}</Tag>,
      width: 80,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v: CallStatus) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{plain(v)}</Tag>,
      width: 160,
    },
    {
      title: 'Ended by',
      key: 'disconnectedBy',
      render: (_: unknown, r) =>
        r.status.startsWith('ended_') ? (
          <Tooltip title={disconnectReasonLabel(r.disconnectReason, r.disconnectedBy, r.endTrigger)}>
            <Tag color={disconnectedByColor(r.disconnectedBy)}>{disconnectedByLabel(r.disconnectedBy)}</Tag>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
      width: 150,
    },
    {
      // The status says a call was dropped; only this says WHY. A client that died
      // during setup reports the stage it reached, so an abandoned first video call
      // is now explicable instead of just "ended_dropped".
      title: 'Why it ended',
      render: (_: unknown, r) => {
        if (!r.endTrigger && !r.failureStage) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        return (
          <Tooltip title={r.failureReason ?? r.endTrigger ?? ''}>
            <span>
              {r.endTrigger && (
                <Tag color={r.failureStage ? 'error' : 'default'}>{plain(r.endTrigger)}</Tag>
              )}
              {r.failureStage && (
                <div style={{ fontSize: 11, opacity: 0.75 }}>
                  stopped after: {plain(r.failureStage)}
                </div>
              )}
            </span>
          </Tooltip>
        );
      },
      width: 220,
    },
    {
      title: 'Duration',
      dataIndex: 'durationSeconds',
      render: (s: number | null) => formatDuration(s),
      width: 100,
    },
    {
      title: 'Charged',
      key: 'charged',
      render: (_: unknown, r) => (
        <span>
          {formatCallCharge(r)}
          {r.billingStatus && r.billingStatus !== 'billed' && (
            <div style={{ fontSize: 11, opacity: 0.75 }}>
              {/* ₹0 has five different meanings; say which one. */}
              <Tag
                color={
                  r.billingStatus === 'verification'
                    ? 'purple'
                    : r.billingStatus === 'unbilled'
                      ? 'warning'
                      : 'default'
                }
                style={{ marginInlineEnd: 0 }}
              >
                {plain(r.billingStatus)}
              </Tag>
            </div>
          )}
          {r.isDummyAttempt && (
            <div style={{ fontSize: 11, opacity: 0.75 }}>
              <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                dummy attempt
              </Tag>
            </div>
          )}
        </span>
      ),
      width: 140,
    },
    {
      title: '',
      render: (_: unknown, r) => (
        <Button size="small" onClick={() => openCall(r.id)}>
          Open
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Calls
      </Typography.Title>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select<StatusFilter | undefined>
            allowClear
            placeholder="Status"
            style={{ minWidth: 200 }}
            value={status}
            onChange={(v) => set({ status: v ?? null })}
            options={[
              { value: 'failed', label: 'failed (dropped / flagged / rejected)' },
              ...(
              [
                'connected',
                'connecting',
                'ringing',
                'initiated',
                'ended_normal',
                'ended_low_balance',
                'ended_dropped',
                'ended_flagged',
                'ended_rejected',
              ] as CallStatus[]
              ).map((v) => ({ value: v, label: plain(v) })),
            ]}
          />
          {since && (
            <Tag closable color="blue" onClose={() => set({ since: null })}>
              {since === 'today' ? 'Started today' : `Since ${formatWhen(since)}`}
            </Tag>
          )}
          <Input.Search
            allowClear
            aria-label="Search calls"
            placeholder="Caller name, @handle, listener name or call id"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onSearch={(v) => set({ q: v.trim() })}
            style={{ minWidth: 280 }}
          />
          <DatePicker.RangePicker
            aria-label="Call date range"
            key={`${from}|${to}`}
            onChange={(_dates, [f, t]) => set({ from: f || null, to: t || null, since: f ? null : since })}
          />
          {(from || to) && (
            <Tag closable color="blue" onClose={() => set({ from: null, to: null })}>
              {from === to ? from : `${from || '…'} → ${to || '…'}`}
            </Tag>
          )}
          <Select<'audio' | 'video' | undefined>
            allowClear
            aria-label="Call mode"
            placeholder="Mode"
            style={{ minWidth: 110 }}
            value={mode}
            onChange={(v) => set({ mode: v ?? null })}
            options={[
              { value: 'audio', label: 'Audio' },
              { value: 'video', label: 'Video' },
            ]}
          />
          <Select<OutcomeFilter | undefined>
            allowClear
            aria-label="Call outcome"
            placeholder="Outcome"
            style={{ minWidth: 190 }}
            value={outcome}
            onChange={(v) => set({ outcome: v ?? null })}
            options={OUTCOME_OPTIONS}
          />
          {userIdFilter && (
            <Tag closable color="blue" onClose={() => set({ userId: null })}>
              One user&apos;s calls ·{' '}
              <UserName
                handle={rows.find((r) => r.userId === userIdFilter)?.userHandle}
                displayName={rows.find((r) => r.userId === userIdFilter)?.userDisplayName}
                userId={userIdFilter}
                secondary="inline"
              />
            </Tag>
          )}
          {listenerIdFilter && (
            <Tag closable color="magenta" onClose={() => set({ listenerId: null })}>
              One listener&apos;s calls ·{' '}
              {(() => {
                const r = rows.find((x) => x.listenerId === listenerIdFilter);
                return (
                  <ListenerName
                    listener={{ displayName: r?.listenerDisplayName, handle: r?.listenerHandle, id: listenerIdFilter }}
                    listenerId={listenerIdFilter}
                    secondary="none"
                  />
                );
              })()}
            </Tag>
          )}
          <Select<VerificationFilter>
            style={{ minWidth: 200 }}
            value={verification}
            onChange={(v) => set({ verification: v === 'all' ? null : v })}
            options={[
              { value: 'all', label: 'Verification: all' },
              { value: 'true', label: 'Verification only' },
              { value: 'false', label: 'Exclude verification' },
            ]}
          />
          <Select<DummyFilter>
            style={{ minWidth: 220 }}
            value={dummy}
            onChange={(v) => set({ dummy: v === 'all' ? null : v })}
            options={[
              { value: 'all', label: 'Dummy attempts: All' },
              { value: 'true', label: 'Dummy attempts only' },
              { value: 'false', label: 'Exclude dummy attempts' },
            ]}
          />
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={() => void exportCsv()}>
            Export CSV
          </Button>
        </Space>
        {/* Quick outcome filter: one tap, with how many calls match the other filters. */}
        <Space size={[6, 8]} wrap style={{ marginBottom: 12 }} data-testid="outcome-chips">
          {(
            [
              { value: undefined, label: 'All' },
              { value: 'connected', label: 'Connected' },
              { value: 'no_answer', label: 'Missed' },
              { value: 'declined', label: 'Declined' },
              { value: 'cancelled', label: 'Cancelled' },
              { value: 'not_connected', label: 'Never connected' },
              { value: 'setup_failed', label: 'Setup failed' },
              // The listeners being rung who never hear it. Worth its own tab:
              // no other filter separates them from a listener who let a
              // perfectly good ring time out.
              { value: 'not_delivered', label: 'Never reached the phone' },
            ] as Array<{ value: OutcomeFilter | undefined; label: string }>
          ).map((o) => {
            const n = outcomeCounts?.[o.value ?? 'all'];
            const active = outcome === o.value;
            return (
              <Tag.CheckableTag
                key={o.value ?? 'all'}
                checked={active}
                onChange={() => set({ outcome: active ? null : (o.value ?? null) })}
                style={{ fontSize: 13, padding: '3px 10px', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                {o.label}
                {n !== undefined && (
                  <Typography.Text type={active ? undefined : 'secondary'} style={{ marginInlineStart: 6, fontSize: 12 }}>
                    {n.toLocaleString('en-IN')}
                  </Typography.Text>
                )}
              </Tag.CheckableTag>
            );
          })}
          <Tag.CheckableTag
            checked={verification === 'true'}
            onChange={(c) => set({ verification: c ? 'true' : null })}
            style={{ fontSize: 13, padding: '3px 10px', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            Verification calls
          </Tag.CheckableTag>
        </Space>
        {list.isError && !list.data ? (
          <QueryError error={list.error} what="calls" onRetry={() => void list.refetch()} />
        ) : (
          <Table
            rowKey="id"
            loading={list.isLoading}
            dataSource={rows}
            columns={columns}
            scroll={{ x: 'max-content' }}
            onRow={(r) => ({ onDoubleClick: () => openCall(r.id) })}
            locale={{ emptyText: 'No calls match these filters.' }}
            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100] }}
          />
        )}
        {list.hasNextPage && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Button loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              Load older calls ({rows.length.toLocaleString('en-IN')} loaded)
            </Button>
          </div>
        )}
      </Card>

      <Drawer
        width={900}
        open={!!selectedId}
        onClose={closeCall}
        title={detail.data ? `Call ${detail.data.id.slice(0, 8)}` : 'Call'}
        extra={
          detail.data && !detail.data.status.startsWith('ended_') ? (
            <Can permission="calls.force_end">
              <Button danger onClick={() => setForceEndOpen(true)}>
                Force end
              </Button>
            </Can>
          ) : null
        }
      >
        {detail.data ? (
          <CallDetailView call={detail.data} />
        ) : detail.isError ? (
          <QueryError error={detail.error} what="this call" onRetry={() => void detail.refetch()} />
        ) : detail.isLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : null}
      </Drawer>

      <Modal
        title="Force-end call"
        open={forceEndOpen}
        onCancel={() => setForceEndOpen(false)}
        onOk={() => forceEnd.mutate()}
        okText="Force end"
        okButtonProps={{
          danger: true,
          disabled: reason.trim().length < 10,
          loading: forceEnd.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Typography.Text>
            This ends the active call immediately, refunds the user from platform_revenue, and tears
            down the Agora channel. The listener keeps any earnings already accrued. Both parties
            see a vague safety message.
          </Typography.Text>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (≥ 10 chars) — written to audit log"
          />
        </Space>
      </Modal>
    </Space>
  );
}
